import * as React from 'react';
import '../../scss/00_styleguide/_faux-input.scss';
import { FormCell, extractFormCellProps, IFormCellProps } from '../FormCell';
import { AreaInputStyled } from './AreaInput.styles';
import { mergeTests } from '../../test-utilities/mergeTests';

// TODO: convert AreaInput to functional component

export interface IAreaInputProps extends IFormCellProps {
  /** Specifies is the textarea should automatically get focus. It also works after initializing the component using the imperative approach 'false -> true'. */
  autoFocus?: boolean;
  /** The value attribute of underlying textarea. */
  value: string | null;
  /** A callback triggered when focus is removed. */
  onBlur?: (event: React.FocusEvent<HTMLTextAreaElement>) => void;
  /** A callback fired when the value prop changes. */
  onChange?: (newValue: string | null, error?: string | null) => void;
  /** Text that will appear in an empty input. */
  placeholder?: string;
  /** Specifies the visible number of lines in underlying textarea. */
  rows?: number;
  /** Visually highlights the component as in error. */
  showErrors?: boolean;
  /** Switches the input to read only which prevents editing the contents. */
  readOnly?: boolean;
  /** This callback exposes the input reference / DOM node to parent components. */
  getInputRef?: (elem: HTMLTextAreaElement) => void;
}

interface IAreaInputState {
  value: string;
}

/**
 * The HTML textarea with a label and error messaging.
 */
export class AreaInput extends React.Component<IAreaInputProps, IAreaInputState> {
  public static defaultProps: Partial<IAreaInputProps> = {
    value: '',
    showErrors: true,
  };

  public state: IAreaInputState = {
    value: this.props.value || '',
  };

  public textInput: HTMLTextAreaElement | null = null;

  public componentDidUpdate(nextProps: IAreaInputProps) {
    if (nextProps.autoFocus && nextProps.autoFocus !== this.props.autoFocus) {
      this.doAutoFocus(nextProps.autoFocus);
    }
  }

  public componentWillReceiveProps(nextProps: IAreaInputProps) {
    if (nextProps.value !== this.state.value) {
      this.setState({ value: nextProps.value || '' });
    }
  }

  public componentDidMount() {
    if (this.props.autoFocus) {
      this.doAutoFocus(this.props.autoFocus);
    }
  }

  public render() {
    const { formCellProps, inputProps } = extractFormCellProps({
      currentCharacterCount: this.state.value.length,
      ...this.props,
    });
    const dataTestID = this.props['data-test-id'];
    const dataTestClass = this.props['data-test-class'] || 'area-input';
    return (
      <FormCell {...formCellProps} data-test-id={dataTestID} data-test-class={dataTestClass}>
        <AreaInputStyled
          {...inputProps}
          onChange={this.onChange}
          showErrorHighlight={!!formCellProps.errorText}
          ref={this.getInputRef}
          value={this.state.value}
          data-test-id={mergeTests(dataTestID, 'area-input')}
          data-test-class={mergeTests(dataTestClass, 'input')}
        />
      </FormCell>
    );
  }

  public onChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    let errorMessage: string | null = null;
    if (this.props.isRequired && !e.target.value && this.props.showErrors) {
      errorMessage = `${this.props.label} cannot be blank`;
    }
    this.setState({
      value: e.target.value,
    });
    if (this.props.onChange) {
      this.props.onChange(e.target.value, errorMessage);
    }
  };

  public doAutoFocus = (autofocus: boolean) => {
    if (autofocus && this.textInput) {
      this.textInput.focus();
    }
  };

  private getInputRef = (input: HTMLTextAreaElement) => {
    this.textInput = input;
    if (this.props.getInputRef) {
      this.props.getInputRef(input);
    }
  };
}
